/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        MobileHomePage.tsx
 * @description Page d'accueil mobile pour les agents de deneigement
 *              Affiche les interventions assignees avec priorite visuelle
 *
 * @features    - Liste des interventions de l'agent connecte
 *              - Tri par priorite (critique > urgente > normale)
 *              - Indicateurs visuels de statut et progression
 *              - Acces rapide a l'intervention en cours
 *
 * @version     2.2
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { deneignementService } from '@/services/deneignementService';
import { zoneService } from '@/services/zoneService';
import { clsx } from 'clsx';
import {
  MapPinIcon,
  ClockIcon,
  BuildingOfficeIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  UserCircleIcon,
  TruckIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { PlayIcon as PlayIconSolid } from '@heroicons/react/24/solid';
import { PRIORITE_LABELS, STATUT_LABELS, STATUT_COLORS, type Deneignement } from '@/types';
import { OfflineIndicator, MobileNavBar, MobileMeteoWidget } from '@/components/mobile';

/**
 * Page d'accueil mobile pour les agents terrain
 * Affiche les interventions assignees triees par priorite
 */
export function MobileHomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  // Charger les interventions de l'agent
  const {
    data: interventionsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['agent-interventions'],
    queryFn: () =>
      deneignementService.list(
        { statut: 'affectee' }, // Filtrer par statut affectee ou en_cours
        1,
        50
      ),
    refetchInterval: 30000, // Rafraichir toutes les 30 secondes
  });

  // Charger aussi les interventions en cours
  const { data: enCoursData } = useQuery({
    queryKey: ['agent-interventions-en-cours'],
    queryFn: () => deneignementService.list({ statut: 'en_cours' }, 1, 50),
    refetchInterval: 15000,
  });

  const interventions = [
    ...(enCoursData?.data || []),
    ...(interventionsData?.data || []),
  ];

  // Trier par priorite puis par heure souhaitee
  const sortedInterventions = interventions.sort((a, b) => {
    const prioriteOrder = { critique: 0, urgente: 1, normale: 2 };
    const prioriteDiff = prioriteOrder[a.priorite] - prioriteOrder[b.priorite];
    if (prioriteDiff !== 0) return prioriteDiff;
    return a.heureSouhaitee.localeCompare(b.heureSouhaitee);
  });

  // Separer les interventions en cours et a venir
  const enCours = sortedInterventions.filter((i) => i.statut === 'en_cours');
  const aVenir = sortedInterventions.filter((i) => i.statut !== 'en_cours');

  const getPrioriteColor = (priorite: string) => {
    switch (priorite) {
      case 'critique':
        return 'bg-red-500 text-white';
      case 'urgente':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'en_cours':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'affectee':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'en_deplacement':
        return 'bg-violet-100 text-violet-700 border-violet-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Header */}
      <header className="bg-primary-600 text-white px-4 py-4 safe-area-inset-top">
        {/* Ligne meteo */}
        <div className="flex items-center justify-between mb-3">
          <MobileMeteoWidget icao="LFPO" />
          <button
            onClick={() => refetch()}
            className="p-2 rounded-full bg-primary-500 hover:bg-primary-400 active:bg-primary-700"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        {/* Infos agent */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
            <UserCircleIcon className="w-9 h-9" />
          </div>
          <div className="flex-1">
            <p className="text-primary-200 text-xs">Bonjour</p>
            <h1 className="text-lg font-bold">{user?.nomComplet || user?.nom || 'Agent'}</h1>
            <p className="text-primary-200 text-xs flex items-center gap-1">
              <TruckIcon className="w-3 h-3" />
              Module Deneigement
            </p>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="p-4 pb-24">
        {/* Section En Cours */}
        {enCours.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              En cours
            </h2>
            <div className="space-y-3">
              {enCours.map((intervention) => (
                <InterventionCard
                  key={intervention.id}
                  intervention={intervention}
                  onClick={() => navigate(`/agent/intervention/${intervention.id}`)}
                  isEnCours
                />
              ))}
            </div>
          </section>
        )}

        {/* Section A Venir */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-gray-500" />
            A venir ({aVenir.length})
          </h2>
          {aVenir.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">Aucune intervention programmee</p>
              <p className="text-sm text-gray-400 mt-1">
                Les nouvelles affectations apparaitront ici
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {aVenir.map((intervention) => (
                <InterventionCard
                  key={intervention.id}
                  intervention={intervention}
                  onClick={() => navigate(`/agent/intervention/${intervention.id}`)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Lien vers liste complete (visible seulement si chef ou regulateur) */}
        {/* Pour les agents terrain, cette liste n'est pas necessaire */}
      </main>

      {/* Barre de navigation mobile */}
      <MobileNavBar />
    </div>
  );
}

/**
 * Carte d'intervention pour la liste mobile
 */
interface InterventionCardProps {
  intervention: Deneignement;
  onClick: () => void;
  isEnCours?: boolean;
}

function InterventionCard({ intervention, onClick, isEnCours }: InterventionCardProps) {
  const prioriteColor = {
    critique: 'border-l-red-500 bg-red-50',
    urgente: 'border-l-orange-500 bg-orange-50',
    normale: 'border-l-gray-400 bg-white',
  }[intervention.priorite];

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left bg-white rounded-xl shadow-sm overflow-hidden transition-all',
        'border-l-4 active:scale-[0.98]',
        prioriteColor,
        isEnCours && 'ring-2 ring-red-500 ring-offset-2'
      )}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-bold text-gray-900">{intervention.numeroOrdre}</p>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <MapPinIcon className="w-4 h-4" />
              <span>{intervention.parking?.code || 'N/A'}</span>
            </div>
          </div>
          <span
            className={clsx(
              'px-2.5 py-1 rounded-full text-xs font-semibold',
              intervention.priorite === 'critique' && 'bg-red-500 text-white',
              intervention.priorite === 'urgente' && 'bg-orange-500 text-white',
              intervention.priorite === 'normale' && 'bg-gray-200 text-gray-700'
            )}
          >
            {PRIORITE_LABELS[intervention.priorite]}
          </span>
        </div>

        {/* Infos */}
        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
          <div className="flex items-center gap-1.5 text-gray-600">
            <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
            <span>{intervention.compagnie.codeIata}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <ClockIcon className="w-4 h-4 text-gray-400" />
            <span>
              {new Date(intervention.heureSouhaitee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {/* Immatriculation avion : affichee seulement si connue, pour ne pas surcharger
              la carte quand l'intervention se fait sans avion present */}
          {intervention.immatriculationAvion && (
            <div className="flex items-center gap-1.5 text-gray-600 col-span-2">
              <PaperAirplaneIcon className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{intervention.immatriculationAvion}</span>
            </div>
          )}
        </div>

        {/* Footer avec bouton */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span
            className={clsx(
              'px-2 py-1 rounded text-xs font-medium',
              STATUT_COLORS[intervention.statut],
              'text-white'
            )}
          >
            {STATUT_LABELS[intervention.statut]}
          </span>
          <div className="flex items-center gap-1 text-primary-600 font-medium text-sm">
            {isEnCours ? (
              <>
                <PlayIconSolid className="w-5 h-5" />
                <span>Continuer</span>
              </>
            ) : (
              <>
                <span>Ouvrir</span>
                <ArrowRightIcon className="w-4 h-4" />
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export default MobileHomePage;
